import { getWorkflowCapabilities } from '@jira/project-settings__workflow-services-capabilities/src/getWorkflowCapabilities.tsx';
import type { WorkflowCapabilities } from '@jira/project-settings__workflow-types/src/workflowTypes.types.tsx';
import type { GraphQLError } from 'graphql';
import { setMark } from '@atlassian/jira-common-performance/src/marks.tsx';
// eslint-disable-next-line jira/import-whitelist
import {
	prefetchApiReadyMark,
	prefetchApiStartMark,
} from '@atlassian/jira-providers-spa-apdex-analytics/src/marks.tsx';
import { createResourceError } from '@atlassian/jira-router-resources-utils/src/utils/create-resource-error/index.tsx';
import type { ResourceStoreContext, RouterDataContext } from '@atlassian/react-resource-router';
import { performPostRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import { QUERY } from './gql.tsx';

type ProjectResponse = {
	data?: {
		jira_projectByIdOrKey?: { projectId: string } | null;
	} | null;
	errors?: ReadonlyArray<GraphQLError>;
};

export const fetchWorkflowCapabilities = async (
	routerContext: RouterDataContext,
	{ tenantContext }: ResourceStoreContext,
) => {
	const { match } = routerContext;

	const { isAnonymous, cloudId } = tenantContext;

	if (isAnonymous) {
		throw new Error("Workflows can't be edited in anonymous mode.");
	}
	if (!match.params.workflowId && !match.params.projectKey && !match.params.issueTypeId) {
		throw new Error('Workflow ID, project Key, or issue type ID is not defined.');
	}

	try {
		// better way to monitor perf?
		setMark(prefetchApiStartMark);

		let workflowCapabilities: WorkflowCapabilities | null = null;
		if (match.params.workflowId) {
			workflowCapabilities = await getWorkflowCapabilities(cloudId, {
				workflowId: match.params.workflowId,
			});
		} else if (match.params.projectKey && match.params.issueTypeId) {
			const { data, errors } = await performPostRequest<ProjectResponse>(
				'/gateway/api/graphql?operation=JiraProjectIdForWorkflowCapabilities',
				{
					method: 'POST',
					headers: {
						'content-type': 'application/json',
						'X-ExperimentalApi': 'JiraProject, projectStyle',
					},
					body: JSON.stringify({
						query: QUERY,
						variables: {
							cloudId,
							projectIdOrKey: match.params.projectKey,
						},
					}),
				},
			);
			if (errors) {
				throw errors;
			}

			if (!data || !data.jira_projectByIdOrKey?.projectId) {
				throw new Error('Project ID is not defined.');
			}

			workflowCapabilities = await getWorkflowCapabilities(cloudId, {
				projectId: String(data.jira_projectByIdOrKey.projectId),
				issueTypeId: match.params.issueTypeId,
			});
		}
		if (!workflowCapabilities) {
			throw new Error('Workflow ID, project ID, or issue type ID is not defined.');
		}

		setMark(prefetchApiReadyMark);
		return workflowCapabilities;

		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (error: any) {
		throw createResourceError(
			{
				component: 'spa.view.routes.admin-pages.workflows.workflow-capabilities.getData',
				message: 'An error was thrown while prefetching workflow capabilities data',
				name: 'ResourceError',
			},
			error,
		);
	}
};
